import type { VerdictV2 } from '../types';
import { SafetyScoreCard, BrandSafetyAgentCard, RiskBreakdownCard } from '../shared';
import { IgSafetySummaryCard } from './IgSafetySummaryCard';
import { IgPostCard } from './IgPostCard';

/* ─── InstagramBrandSafetyDashboard ──────────────────────────────────────────
 * IG redesign mirroring the Figma BS flow:
 *   1. Safety Score card  (shared SafetyGauge + verdict cluster)
 *   2. Brand Safety Agent card  (shared BrandSafetyAgentCard)
 *   3. Risk Breakdown  (shared RiskBreakdownCard ← verdictV2.category_scores)
 *   4. Safety Summary  (IgSafetySummaryCard — rings + stat cells)
 *   5. Posts list  (IgPostCard — rich per-post cards)
 * Every section guards on its own data and renders nothing when absent. */

const TIER_HEADLINE: Record<string, string> = {
  safe: 'Clean and brand safe',
  moderate: 'Mostly safe — review the flags',
  risky: 'Brand safety risks found',
  blocked: 'Blocked — serious risks found',
  needs_review: 'Needs human review',
};

const TIER_CHIP_COLOR: Record<string, string> = {
  safe: '#059669',
  moderate: '#D97706',
  risky: '#DC2626',
  blocked: '#DC2626',
  needs_review: '#D97706',
};

interface IgData {
  profile_meta?: { posts_count?: number };
  summary?: Record<string, number | undefined>;
  posts?: unknown[];
}

export function InstagramBrandSafetyDashboard({
  data,
  verdictV2,
}: {
  data: IgData;
  verdictV2?: VerdictV2 | null;
}) {
  const summary = data?.summary ?? {};
  const posts = (data?.posts ?? []) as Array<Record<string, unknown>>;
  const heroScore: number | null =
    verdictV2?.overall_score ?? (summary.creator_safety_score as number | undefined) ?? null;

  const tier = verdictV2?.tier;
  const chipColor = tier ? TIER_CHIP_COLOR[tier] ?? '#737373' : '#737373';
  const hardBlocks = verdictV2?.hard_blocks ?? [];
  const flaggedCount = hardBlocks.length;
  const postsScanned = (summary.posts_analyzed as number | undefined) ?? posts.length;

  const agentChips: { label: string; color: string }[] = verdictV2
    ? [
        {
          label:
            verdictV2.overall_score != null
              ? `${tier ? tier.replace(/_/g, ' ') : 'Reviewed'} · ${verdictV2.overall_score}/100`
              : tier
                ? tier.replace(/_/g, ' ')
                : 'Reviewed',
          color: chipColor,
        },
        {
          label:
            flaggedCount === 0
              ? 'Zero hard blocks'
              : `${flaggedCount} hard block${flaggedCount !== 1 ? 's' : ''}`,
          color: chipColor,
        },
        ...(verdictV2.needs_human_review
          ? [{ label: 'Human review required', color: '#D97706' }]
          : []),
      ]
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 1+2. Safety Score + Brand Safety Agent side-by-side (Figma 1741:55356,
          matches the YT CreatorProfileSummary layout; reuses the shared
          SafetyScoreCard with its "N risk categories flagged" pill). The old
          bespoke score card (RatingBadge + confidence + human-review banner +
          Total Posts) is dropped — the human-review state lives in the agent
          chips. */}
      {(heroScore != null || verdictV2) && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flexWrap: 'wrap' }}>
          {heroScore != null && <SafetyScoreCard score={heroScore} flaggedCount={flaggedCount} />}
          {verdictV2 && (
            <BrandSafetyAgentCard
              scans={postsScanned}
              scanNoun="posts and comments"
              headline={(tier && TIER_HEADLINE[tier]) || 'Brand safety reviewed'}
              description={
                flaggedCount > 0
                  ? hardBlocks
                      .map((b) => b.evidence?.quote)
                      .filter(Boolean)
                      .slice(0, 2)
                      .join(' · ') || 'Risk signals detected across the analyzed posts.'
                  : 'No hard blocks detected across the analyzed posts and comments.'
              }
              chips={agentChips}
            />
          )}
        </div>
      )}

      {/* 3. Risk Breakdown — shared card from verdictV2.category_scores */}
      {verdictV2?.category_scores && verdictV2.category_scores.length > 0 && (
        <RiskBreakdownCard scores={verdictV2.category_scores} />
      )}

      {/* 4. Safety summary — rings + stat cells */}
      <IgSafetySummaryCard summary={summary} />

      {/* 5. Posts list — rich per-post cards */}
      {posts.length > 0 && (
        <div>
          <div style={{ fontSize: 20, fontWeight: 500, lineHeight: '28px', color: '#171717', marginBottom: 16 }}>
            Recent Post
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {posts.map((p, i) => (
              <IgPostCard key={(p.cache_key as string | undefined) ?? i} post={p} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
